import React, { useState, useMemo, useRef } from 'react';
import { useModalDismiss } from '../../../hooks/useModalDismiss';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from '../../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faClone, faMagic, faTrashAlt, faUserCheck, faPause, faPlay, faClock, faUsers, faUndo, faFileExcel, faDownload, faChevronDown, faFileImport, faLocationDot, faListUl, faTableList } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../contexts/AuthContext';
import AddCellModal from './AddCellModal';
import ConfirmationModal from './ConfirmationModal';
import AlertModal from './AlertModal';
import AssignSupervisorModal from './AssignSupervisorModal';
import ExcelAssignmentModal from './ExcelAssignmentModal';
import LocationImportModal from './LocationImportModal';
import LocationManageModal from './LocationManageModal';
import AddressesMapView from './AddressesMapView';
import DetailImportModal from './DetailImportModal';
import DetailsView from './DetailsView';

const QuotaMatrix = ({
  quotaProjectId,
  dimensions,
  cells,
  contextFilters,
  calculator,
  onCellSelect,
  onCellAdded,
  onCellUpdated,
  onCellDeleted,
  overallTarget,
  isLocked = false,
  assignmentStatus = null, // New prop for assignment status
  projectStatus = null, // New prop for project status
  pendingAssignments = [], // New prop for pending assignments
  pendingModifications = [], // New prop for pending cell modifications
  onCancelPendingDeletion = null, // Callback to cancel a pending deletion
  onBulkAssignSupervisors = null, // Callback for bulk assign button
  onImportComplete = null // Callback after a successful Excel assignment import
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Check assignment permission based on role
  // chef_terrain can always assign (no delegation needed), blocked only by pending_approval or locked/finished/archived
  const projectLockedFinishedOrArchived = projectStatus && ['locked', 'finished', 'archived'].includes(projectStatus);
  const canAssignSupervisor = user && assignmentStatus !== 'pending_approval' && !projectLockedFinishedOrArchived && (
    (user.role === 'chef_terrain_superieur') ||
    (user.role === 'chef_terrain')
  );
  // Downloading the assignment workbook is read-only, so it stays available once the
  // project leaves draft (locked, in progress, finished...) — only the import half of the
  // menu is gated on canAssignSupervisor.
  const canDownloadAssignments = user && (user.role === 'chef_terrain' || user.role === 'chef_terrain_superieur');
  const canTogglePause = user && (user.role === 'chef_terrain' || user.role === 'chef_terrain_superieur' || user.role === 'administrateur_general');
  const [showAddModal, setShowAddModal] = useState(false);
  const [togglingPause, setTogglingPause] = useState(null); // cell id being toggled
  const [editingCell, setEditingCell] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateInfo, setGenerateInfo] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const generateDismiss = useModalDismiss(() => setShowGenerateModal(false), { enabled: showGenerateModal });
  const successDismiss = useModalDismiss(() => setShowSuccessModal(false), { enabled: showSuccessModal });
  const [successInfo, setSuccessInfo] = useState(null);
  const [selectedCells, setSelectedCells] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteCellId, setDeleteCellId] = useState(null);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '', type: 'info' });
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningCell, setAssigningCell] = useState(null);
  const [showExcelMenu, setShowExcelMenu] = useState(false);
  const [showExcelImport, setShowExcelImport] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [showLocationMenu, setShowLocationMenu] = useState(false);
  const [showLocationImport, setShowLocationImport] = useState(false);
  const [showLocationManage, setShowLocationManage] = useState(false);
  const [mapRefreshKey, setMapRefreshKey] = useState(0);
  // Détails : free-form sheet of extras (staff-only, never shown to prestataires).
  const [showDetailMenu, setShowDetailMenu] = useState(false);
  const [showDetailImport, setShowDetailImport] = useState(false);
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);
  const [detailRowCount, setDetailRowCount] = useState(0);
  const [downloadingDetailTemplate, setDownloadingDetailTemplate] = useState(false);
  const [downloadingDetailExport, setDownloadingDetailExport] = useState(false);
  // After an address import/delete: refresh the embedded map AND the builder (pending state).
  const handleAddressesChanged = async (data) => {
    setMapRefreshKey(k => k + 1);
    if (onImportComplete) await onImportComplete(data);
  };
  const [downloadingLocationTemplate, setDownloadingLocationTemplate] = useState(false);
  // Header dropdowns are portaled to <body> so overflow:auto / stacking contexts
  // (the map, the dimensions panel) can't clip or hide them.
  const excelBtnRef = useRef(null);
  const locationBtnRef = useRef(null);
  const detailBtnRef = useRef(null);
  const [excelMenuStyle, setExcelMenuStyle] = useState(null);
  const [locationMenuStyle, setLocationMenuStyle] = useState(null);
  const [detailMenuStyle, setDetailMenuStyle] = useState(null);
  const openHeaderMenu = (btnRef, setStyle, setShow) => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      setStyle({ position: 'fixed', top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
    }
    setShow(true);
  };
  // Display mode for the cells table: 'pivot' (Synthèse — prestataires × combinaisons)
  // or 'list' (Gestion — classic one-row-per-combinaison with all management actions).
  // Draft projects open on Gestion (list) for editing; others open on Synthèse (pivot).
  const [viewMode, setViewMode] = useState(projectStatus === 'draft' ? 'list' : 'pivot');

  // Generate all possible combinations from dimensions (cartesian product)
  const generateAllCombinations = () => {
    if (!dimensions || dimensions.length === 0) {
      return [];
    }

    // Filter dimensions to only include required ones or all if none are required
    const activeDimensions = dimensions.filter(d => 
      d.is_required !== false && d.values_json && d.values_json.length > 0
    );

    if (activeDimensions.length === 0) {
      return [];
    }

    // Get all active values for each dimension, excluding those with target_quota = 0
    const dimensionValues = activeDimensions.map(dim => ({
      dimensionId: dim.id,
      dimension: dim,
      values: dim.values_json.filter(v => 
        v.is_active !== false && 
        (v.target_quota === undefined || v.target_quota === null || parseInt(v.target_quota) > 0)
      )
    }));

    // Generate cartesian product
    const combinations = [];
    
    const generateRecursive = (currentCombo, dimIndex) => {
      if (dimIndex >= dimensionValues.length) {
        combinations.push({ ...currentCombo });
        return;
      }

      const { dimensionId, values } = dimensionValues[dimIndex];
      for (const value of values) {
        generateRecursive(
          { ...currentCombo, [dimensionId]: value.code },
          dimIndex + 1
        );
      }
    };

    generateRecursive({}, 0);
    return combinations;
  };

  const handleGenerateAllCells = async () => {
    if (!dimensions || dimensions.length === 0) {
      setAlertModal({
        show: true,
        title: 'Dimensions requises',
        message: 'Veuillez d\'abord créer des dimensions',
        type: 'warning'
      });
      return;
    }

    if (!overallTarget || overallTarget <= 0) {
      setAlertModal({
        show: true,
        title: 'Objectif requis',
        message: 'L\'objectif global doit être défini',
        type: 'warning'
      });
      return;
    }

    const combinations = generateAllCombinations();
    
    if (combinations.length === 0) {
      setAlertModal({
        show: true,
        title: 'Aucune combinaison',
        message: 'Aucune combinaison possible avec les dimensions définies (vérifiez que les valeurs ont des quotas > 0)',
        type: 'warning'
      });
      return;
    }

    // Calculate proportional quota distribution
    // Algorithm: For each combination, calculate quota based on proportional distribution
    // Example: exclusif_e6=99, une_rech_E6_3M=100
    // Cell quota = (99 * 100) / overallTarget = 9900 / 999 = ~9.9 -> 10
    // This ensures each dimension value's total across all cells equals its target_quota
    
    // Step 1: Build a map of dimension value quotas and validate
    const valueQuotaMap = {}; // {dimId: {valueCode: quota}}
    
    // Validate that each dimension's quotas sum to overallTarget
    for (const dim of dimensions) {
      if (!valueQuotaMap[dim.id]) {
        valueQuotaMap[dim.id] = {};
      }
      
      let dimTotal = 0;
      dim.values_json?.forEach(value => {
        if (value.is_active !== false && value.target_quota !== undefined && value.target_quota !== null) {
          const quota = parseInt(value.target_quota) || 0;
          if (quota > 0) {
            valueQuotaMap[dim.id][value.code] = quota;
            dimTotal += quota;
          }
        }
      });
      
      // Validate that dimension quotas sum to overallTarget
      if (dimTotal !== overallTarget) {
        setAlertModal({
          show: true,
          title: 'Quota invalide',
          message: `Les quotas de la dimension "${dim.label}" (total: ${dimTotal}) ne correspondent pas au quota générale (${overallTarget}). Veuillez ajuster les quotas des valeurs.`,
          type: 'error'
        });
        return;
      }
    }

    // Step 2: Calculate initial quotas using different methods based on number of dimensions
    
    let combinationsWithQuota = [];
    
    if (dimensions.length === 2) {
      // For 2 dimensions: use independence allocation
      // q(cell) = (quota(valueA) * quota(valueB)) / T
      combinationsWithQuota = combinations.map(combo => {
        const dimIds = Object.keys(combo);
        if (dimIds.length !== 2) {
          return { ...combo, calculatedQuota: 0 };
        }
        
        const quota1 = valueQuotaMap[dimIds[0]]?.[combo[dimIds[0]]] || 0;
        const quota2 = valueQuotaMap[dimIds[1]]?.[combo[dimIds[1]]] || 0;
        
        if (quota1 <= 0 || quota2 <= 0 || overallTarget <= 0) {
          return { ...combo, calculatedQuota: 0 };
        }
        
        const cellQuota = (quota1 * quota2) / overallTarget;
        return {
          ...combo,
          calculatedQuota: cellQuota // Keep as float for now, will round later
        };
      });
    } else if (dimensions.length >= 3) {
      // For 3+ dimensions: use Iterative Proportional Fitting (IPF / raking)
      // Start each valid cell with weight = 1
      // Then iterate: scale all cells so each dimension's margins match targets
      
      // Filter out invalid combinations (where any dimension value has quota 0)
      const validCombinations = combinations.filter(combo => {
        return Object.entries(combo).every(([dimId, valueCode]) => {
          const quota = valueQuotaMap[dimId]?.[valueCode];
          return quota !== undefined && quota > 0;
        });
      });
      
      // Initialize all valid cells with weight = 1
      combinationsWithQuota = validCombinations.map(combo => {
        return {
          ...combo,
          calculatedQuota: 1.0 // Start with weight = 1
        };
      });
      
      // IPF iterations
      const maxIterations = 1000;
      const tolerance = 0.0001; // Convergence tolerance (stricter)
      
      for (let iter = 0; iter < maxIterations; iter++) {
        let maxError = 0;
        let hasConverged = true;
        
        // Iterate through each dimension and adjust
        for (const dim of dimensions) {
          const dimId = dim.id.toString();
          
          // Calculate current totals for each value in this dimension
          const currentTotals = {};
          const targetTotals = {};
          
          dim.values_json?.forEach(value => {
            if (value.target_quota !== undefined && value.target_quota !== null) {
              const valueCode = value.code;
              const targetQuota = parseInt(value.target_quota) || 0;
              targetTotals[valueCode] = targetQuota;
              
              // Calculate current total for this dimension value
              currentTotals[valueCode] = combinationsWithQuota
                .filter(combo => combo[dimId] === valueCode)
                .reduce((sum, combo) => sum + (combo.calculatedQuota || 0), 0);
            }
          });
          
          // Scale each combination to match target quotas for this dimension
          combinationsWithQuota.forEach(combo => {
            const valueCode = combo[dimId];
            const targetQuota = targetTotals[valueCode] || 0;
            const currentTotal = currentTotals[valueCode] || 0;
            
            if (targetQuota === 0) {
              // Set to 0 if target is 0
              combo.calculatedQuota = 0;
            } else if (currentTotal > 0) {
              // Scale proportionally
              const scaleFactor = targetQuota / currentTotal;
              combo.calculatedQuota = (combo.calculatedQuota || 0) * scaleFactor;
              
              // Track convergence
              const error = Math.abs(scaleFactor - 1);
              if (error > tolerance) {
                hasConverged = false;
              }
              maxError = Math.max(maxError, error);
            } else {
              // currentTotal is 0 but targetQuota > 0 - this shouldn't happen with valid combinations
              // but if it does, distribute equally among cells with this value
              const cellsWithValue = combinationsWithQuota.filter(c => c[dimId] === valueCode);
              if (cellsWithValue.length > 0) {
                const perCell = targetQuota / cellsWithValue.length;
                cellsWithValue.forEach(c => {
                  c.calculatedQuota = perCell;
                });
              }
            }
          });
        }
        
        // Check convergence
        if (hasConverged) {
          console.log(`IPF converged after ${iter + 1} iterations with max error: ${maxError}`);
          break;
        }
      }
      
      // Verify all margins match targets exactly (within tolerance)
      const verificationErrors = [];
      dimensions.forEach(dim => {
        const dimId = dim.id.toString();
        dim.values_json?.forEach(value => {
          if (value.target_quota !== undefined && value.target_quota !== null) {
            const valueCode = value.code;
            const targetQuota = parseInt(value.target_quota) || 0;
            const currentTotal = combinationsWithQuota
              .filter(combo => combo[dimId] === valueCode)
              .reduce((sum, combo) => sum + (combo.calculatedQuota || 0), 0);
            
            const error = Math.abs(currentTotal - targetQuota);
            if (error > tolerance && targetQuota > 0) {
              verificationErrors.push({
                dimension: dim.label,
                value: value.label || valueCode,
                target: targetQuota,
                actual: currentTotal,
                error: error
              });
            }
          }
        });
      });
      
      if (verificationErrors.length > 0) {
        console.warn('IPF verification errors:', verificationErrors);
      }
      
      // Final normalization: scale to match overall target exactly
      // This preserves the ratios while ensuring total = overallTarget
      const currentOverallTotal = combinationsWithQuota.reduce((sum, c) => sum + (c.calculatedQuota || 0), 0);
      if (currentOverallTotal > 0 && Math.abs(currentOverallTotal - overallTarget) > tolerance) {
        const normalizationFactor = overallTarget / currentOverallTotal;
        combinationsWithQuota.forEach(combo => {
          combo.calculatedQuota = (combo.calculatedQuota || 0) * normalizationFactor;
        });
      }
    } else {
      // 1 dimension: assign quotas directly
      combinationsWithQuota = combinations.map(combo => {
        const dimId = Object.keys(combo)[0];
        const valueCode = combo[dimId];
        const quota = valueQuotaMap[dimId]?.[valueCode] || 0;
        return {
          ...combo,
          calculatedQuota: quota
        };
      });
    }

    // Step 3: Controlled rounding using Iterative Proportional Rounding (IPR)
    // This preserves all margin constraints while rounding to integers
    // We use a controlled rounding algorithm that respects all dimension constraints
    
    // First, round all to floor values
    combinationsWithQuota.forEach(combo => {
      combo.calculatedQuota = Math.max(0, Math.floor(combo.calculatedQuota || 0));
      combo._fractionalPart = (combo.calculatedQuota || 0) - Math.floor(combo.calculatedQuota || 0);
    });
    
    // Build target maps
    const valueTargets = {}; // {dimId: {valueCode: targetTotal}}
    dimensions.forEach(dim => {
      if (!valueTargets[dim.id]) {
        valueTargets[dim.id] = {};
      }
      dim.values_json?.forEach(value => {
        if (value.is_active !== false && value.target_quota !== undefined && value.target_quota !== null) {
          const quota = parseInt(value.target_quota) || 0;
          if (quota > 0) {
            valueTargets[dim.id][value.code] = quota;
          }
        }
      });
    });
    
    // Iterative Proportional Rounding: iteratively adjust to satisfy all constraints
    const maxRoundingIterations = 100;
    
    for (let roundIter = 0; roundIter < maxRoundingIterations; roundIter++) {
      let allMarginsSatisfied = true;
      let adjustmentsMade = false;
      
      // Check each dimension
      for (const dim of dimensions) {
        const dimId = dim.id.toString();
        
        Object.keys(valueTargets[dim.id] || {}).forEach(valueCode => {
          const target = valueTargets[dim.id][valueCode];
          
          // Calculate current integer total
          const currentTotal = combinationsWithQuota
            .filter(combo => combo[dimId] === valueCode)
            .reduce((sum, combo) => sum + (combo.calculatedQuota || 0), 0);
          
          const diff = target - currentTotal;
          
          if (Math.abs(diff) > 0.5) {
            allMarginsSatisfied = false;
            adjustmentsMade = true;
            
            // Find all cells with this dimension value
            const relevantCombinations = combinationsWithQuota.filter(combo => {
              return combo[dimId] === valueCode;
            });
            
            if (relevantCombinations.length === 0) return;
            
            if (diff > 0) {
              // Need to add quota - prioritize cells with largest fractional parts
              // Sort by fractional part (descending), then by current quota (descending)
              const sorted = [...relevantCombinations].sort((a, b) => {
                const fracA = a._fractionalPart || 0;
                const fracB = b._fractionalPart || 0;
                if (Math.abs(fracA - fracB) > 0.0001) {
                  return fracB - fracA;
                }
                return (b.calculatedQuota || 0) - (a.calculatedQuota || 0);
              });
              
              // Add to cells with largest fractional parts
              let remaining = Math.round(diff);
              for (const combo of sorted) {
                if (remaining <= 0) break;
                combo.calculatedQuota += 1;
                remaining--;
              }
            } else {
              // Need to remove quota - prioritize cells with smallest fractional parts and smallest quotas
              const sorted = [...relevantCombinations].sort((a, b) => {
                const fracA = a._fractionalPart || 0;
                const fracB = b._fractionalPart || 0;
                if (Math.abs(fracA - fracB) > 0.0001) {
                  return fracA - fracB; // Smallest fractional parts first
                }
                return (a.calculatedQuota || 0) - (b.calculatedQuota || 0);
              });
              
              let remaining = Math.round(Math.abs(diff));
              for (const combo of sorted) {
                if (remaining <= 0) break;
                if (combo.calculatedQuota > 0) {
                  combo.calculatedQuota -= 1;
                  remaining--;
                }
              }
            }
          }
        });
      }
      
      // Check overall total
      const currentOverallTotal = combinationsWithQuota.reduce((sum, c) => sum + (c.calculatedQuota || 0), 0);
      const overallDiff = overallTarget - currentOverallTotal;
      
      if (Math.abs(overallDiff) > 0.5) {
        allMarginsSatisfied = false;
        adjustmentsMade = true;
        
        // Adjust overall total by modifying cells proportionally
        if (overallDiff > 0) {
          // Add to cells with largest fractional parts
          const sorted = [...combinationsWithQuota].sort((a, b) => {
            const fracA = a._fractionalPart || 0;
            const fracB = b._fractionalPart || 0;
            if (Math.abs(fracA - fracB) > 0.0001) {
              return fracB - fracA;
            }
            return (b.calculatedQuota || 0) - (a.calculatedQuota || 0);
          });
          
          let remaining = Math.round(overallDiff);
          for (const combo of sorted) {
            if (remaining <= 0) break;
            combo.calculatedQuota += 1;
            remaining--;
          }
        } else {
          // Remove from cells with smallest fractional parts
          const sorted = [...combinationsWithQuota].sort((a, b) => {
            const fracA = a._fractionalPart || 0;
            const fracB = b._fractionalPart || 0;
            if (Math.abs(fracA - fracB) > 0.0001) {
              return fracA - fracB;
            }
            return (a.calculatedQuota || 0) - (b.calculatedQuota || 0);
          });
          
          let remaining = Math.round(Math.abs(overallDiff));
          for (const combo of sorted) {
            if (remaining <= 0) break;
            if (combo.calculatedQuota > 0) {
              combo.calculatedQuota -= 1;
              remaining--;
            }
          }
        }
      }
      
      if (allMarginsSatisfied && !adjustmentsMade) {
        break; // All constraints satisfied
      }
      
      // Recalculate fractional parts for next iteration
      combinationsWithQuota.forEach(combo => {
        // Keep track of original fractional part for prioritization
        // But we can't recalculate it since we've already rounded
        // So we use a priority based on current quota size
        combo._fractionalPart = (combo.calculatedQuota || 0) % 1;
      });
    }
    
    // Ensure all quotas are non-negative integers
    combinationsWithQuota.forEach(combo => {
      combo.calculatedQuota = Math.max(0, Math.round(combo.calculatedQuota || 0));
      delete combo._fractionalPart; // Clean up temporary property
    });
    
    // Recalculate totals to verify
    const valueTotals = {};
    dimensions.forEach(dim => {
      if (!valueTotals[dim.id]) {
        valueTotals[dim.id] = {};
      }
      Object.keys(valueTargets[dim.id] || {}).forEach(valueCode => {
        valueTotals[dim.id][valueCode] = 0;
      });
    });
    
    combinationsWithQuota.forEach(combo => {
      Object.entries(combo).forEach(([dimId, valueCode]) => {
        if (dimId !== 'calculatedQuota' && valueTotals[dimId] && valueTotals[dimId][valueCode] !== undefined) {
          valueTotals[dimId][valueCode] += combo.calculatedQuota || 0;
        }
      });
    });
    
    // Final adjustment: ensure overall total matches exactly using iterative adjustment
    // We need to balance overall total and dimension value totals
    // Use a few iterations to converge
    
    let maxIterations = 50;
    for (let iter = 0; iter < maxIterations; iter++) {
      // Calculate current totals
      const currentValueTotals = {};
      dimensions.forEach(dim => {
        if (!currentValueTotals[dim.id]) {
          currentValueTotals[dim.id] = {};
        }
        Object.keys(valueTargets[dim.id] || {}).forEach(valueCode => {
          currentValueTotals[dim.id][valueCode] = 0;
        });
      });
      
      combinationsWithQuota.forEach(combo => {
        Object.entries(combo).forEach(([dimId, valueCode]) => {
          if (dimId !== 'calculatedQuota' && currentValueTotals[dimId] && currentValueTotals[dimId][valueCode] !== undefined) {
            currentValueTotals[dimId][valueCode] += combo.calculatedQuota || 0;
          }
        });
      });
      
      let currentOverallTotal = combinationsWithQuota.reduce((sum, c) => sum + (c.calculatedQuota || 0), 0);
      let overallDiff = overallTarget - currentOverallTotal;
      
      let hasChanges = false;
      
      // First priority: adjust overall total if it's off by more than 1
      if (Math.abs(overallDiff) > 1 && combinationsWithQuota.length > 0) {
        hasChanges = true;
        
        if (overallDiff > 0) {
          // Need to add quota - add to cells that can accept it (not at dimension limits)
          const candidates = combinationsWithQuota.filter(combo => {
            // Check if adding to this combo won't break dimension constraints too much
            let canAdd = true;
            Object.entries(combo).forEach(([dimId, valueCode]) => {
              if (dimId !== 'calculatedQuota') {
                const currentTotal = currentValueTotals[dimId]?.[valueCode] || 0;
                const target = valueTargets[dimId]?.[valueCode] || 0;
                // Can add if current < target or if we're close to target
                if (currentTotal >= target) {
                  canAdd = false;
                }
              }
            });
            return canAdd && (combo.calculatedQuota || 0) > 0;
          });
          
          if (candidates.length > 0) {
            // Distribute among candidates proportionally
            const totalCandidateQuota = candidates.reduce((sum, c) => sum + (c.calculatedQuota || 0), 0);
            if (totalCandidateQuota > 0) {
              candidates.forEach(combo => {
                const proportion = (combo.calculatedQuota || 0) / totalCandidateQuota;
                const addition = Math.max(1, Math.round(overallDiff * proportion));
                combo.calculatedQuota = (combo.calculatedQuota || 0) + addition;
              });
            } else {
              // Distribute equally
              const perCell = Math.floor(overallDiff / candidates.length);
              const remainder = overallDiff % candidates.length;
              candidates.forEach((combo, idx) => {
                combo.calculatedQuota = (combo.calculatedQuota || 0) + perCell + (idx < remainder ? 1 : 0);
              });
            }
          }
        } else {
          // Need to remove quota - remove from cells that exceed dimension targets
          const candidates = combinationsWithQuota.filter(combo => {
            // Prefer cells that are contributing to dimension totals that exceed targets
            let shouldReduce = false;
            Object.entries(combo).forEach(([dimId, valueCode]) => {
              if (dimId !== 'calculatedQuota') {
                const currentTotal = currentValueTotals[dimId]?.[valueCode] || 0;
                const target = valueTargets[dimId]?.[valueCode] || 0;
                if (currentTotal > target) {
                  shouldReduce = true;
                }
              }
            });
            return shouldReduce && (combo.calculatedQuota || 0) > 0;
          });
          
          if (candidates.length === 0) {
            // No cells exceed dimension targets, remove from smallest
            candidates.push(...combinationsWithQuota.filter(c => (c.calculatedQuota || 0) > 0));
            candidates.sort((a, b) => (a.calculatedQuota || 0) - (b.calculatedQuota || 0));
          }
          
          let remaining = Math.abs(overallDiff);
          for (const combo of candidates) {
            if (remaining <= 0) break;
            if (combo.calculatedQuota > 0) {
              const reduction = Math.min(remaining, combo.calculatedQuota);
              combo.calculatedQuota -= reduction;
              remaining -= reduction;
            }
          }
        }
      }
      
      // Second priority: fine-tune dimension value totals (small adjustments)
      dimensions.forEach(dim => {
        Object.keys(valueTargets[dim.id] || {}).forEach(valueCode => {
          const target = valueTargets[dim.id][valueCode];
          const current = currentValueTotals[dim.id]?.[valueCode] || 0;
          const diff = target - current;
          
          // Only make small adjustments (1-2 units) to avoid breaking overall total
          if (Math.abs(diff) > 0 && Math.abs(diff) <= 2) {
            const relevantCombinations = combinationsWithQuota.filter(combo => {
              return combo[dim.id] === valueCode && (combo.calculatedQuota || 0) > 0;
            });
            
            if (relevantCombinations.length > 0) {
              hasChanges = true;
              
              if (diff > 0) {
                // Add to largest cells
                const sorted = [...relevantCombinations].sort((a, b) => 
                  (b.calculatedQuota || 0) - (a.calculatedQuota || 0)
                );
                for (let i = 0; i < diff && i < sorted.length; i++) {
                  sorted[i].calculatedQuota += 1;
                }
              } else {
                // Remove from smallest cells
                const sorted = [...relevantCombinations].sort((a, b) => 
                  (a.calculatedQuota || 0) - (b.calculatedQuota || 0)
                );
                let remaining = Math.abs(diff);
                for (const combo of sorted) {
                  if (remaining <= 0) break;
                  if (combo.calculatedQuota > 0) {
                    combo.calculatedQuota -= 1;
                    remaining--;
                  }
                }
              }
            }
          }
        });
      });
      
      if (!hasChanges) break; // Converged
    }
    
    // Final verification and micro-adjustment for overall total
    let finalOverallTotal = combinationsWithQuota.reduce((sum, c) => sum + (c.calculatedQuota || 0), 0);
    let finalDiff = overallTarget - finalOverallTotal;
    
    // If still off, make final micro-adjustment
    if (Math.abs(finalDiff) > 0 && Math.abs(finalDiff) <= combinationsWithQuota.length) {
      const sorted = [...combinationsWithQuota].filter(c => (c.calculatedQuota || 0) > 0)
        .sort((a, b) => finalDiff > 0 ? b.calculatedQuota - a.calculatedQuota : a.calculatedQuota - b.calculatedQuota);
      
      for (let i = 0; i < Math.abs(finalDiff) && i < sorted.length; i++) {
        if (finalDiff > 0) {
          sorted[i].calculatedQuota += 1;
        } else if (sorted[i].calculatedQuota > 0) {
          sorted[i].calculatedQuota -= 1;
        }
      }
    }
    
    // Step 4: Filter out cells with quota 0 (these should not be created)
    const validCombinations = combinationsWithQuota.filter(c => (c.calculatedQuota || 0) > 0);
    
    // Final total calculation
    const totalAllocated = validCombinations.reduce((sum, c) => sum + (c.calculatedQuota || 0), 0);

    // Check if we have valid combinations
    if (validCombinations.length === 0) {
      setAlertModal({
        show: true,
        title: 'Aucune combinaison valide',
        message: 'Aucune cellule ne peut être générée avec les quotas définis. Vérifiez que les quotas des valeurs de dimension permettent de créer des combinaisons valides.',
        type: 'warning'
      });
      return;
    }
    
    // Show confirmation modal
    setGenerateInfo({
      combinationsCount: validCombinations.length,
      overallTarget,
      targetPerCell: null,
      remainder: 0,
      combinations: validCombinations.map(c => {
        const { calculatedQuota, ...rest } = c;
        return rest;
      }),
      useIndividualQuotas: true,
      combinationsWithQuota: validCombinations,
      totalAllocated
    });
    setShowGenerateModal(true);
  };

  const confirmGenerateCells = async () => {
    if (!generateInfo) return;

    try {
      setGenerating(true);
      setShowGenerateModal(false);

      // Prepare cells data using calculated quotas from combinationsWithQuota
      // Only include cells with quota > 0
      const cellsData = (generateInfo.combinationsWithQuota || [])
        .filter(combo => (combo.calculatedQuota || 0) > 0)
        .map(combo => {
          const { calculatedQuota, ...dimensionValues } = combo;
          return {
            dimension_values_json: dimensionValues,
            target_count: calculatedQuota || 0,
            status: 'pending'
          };
        });

      const response = await axios.post(
        `/quotas/projects/${quotaProjectId}/cells/bulk`,
        { cells: cellsData }
      );

      // Refresh cells list - add all cells at once
      if (response.data.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
        // Add all cells in a single batch update
        onCellAdded(response.data.data);
      }

      const createdCount = response.data.created_count || 0;
      const skippedCount = response.data.skipped_count || 0;

      // Show success modal
      setSuccessInfo({
        createdCount,
        skippedCount
      });
      setShowSuccessModal(true);

      // Reset generate info
      setGenerateInfo(null);
    } catch (error) {
      console.error('Error generating cells:', error);
      setShowGenerateModal(false);
      setGenerateInfo(null);
      // Show error modal
      setSuccessInfo({
        isError: true,
        message: error.response?.data?.message || 'Erreur lors de la génération des cellules'
      });
      setShowSuccessModal(true);
    } finally {
      setGenerating(false);
    }
  };

  // Filter cells based on context filters
  const filteredCells = useMemo(() => {
    if (!contextFilters || Object.keys(contextFilters).length === 0) {
      return cells;
    }
    return cells.filter(cell => {
      const cellValues = cell.dimension_values_json || {};
      return Object.entries(contextFilters).every(([dimId, valueCode]) => {
        return cellValues[dimId.toString()] === valueCode;
      });
    });
  }, [cells, contextFilters]);

  const handleCellUpdate = async (cellId, updates) => {
    try {
      const response = await axios.put(
        `/quotas/projects/${quotaProjectId}/cells/${cellId}`,
        updates
      );
      onCellUpdated(response.data.data);
    } catch (error) {
      console.error('Error updating cell:', error);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: 'Erreur lors de la mise à jour',
        type: 'error'
      });
    }
  };

  const handleCellDelete = (cellId) => {
    const cell = cells.find(c => c.id === cellId);
    if (cell && cell.current_count > 0) {
      setAlertModal({
        show: true,
        title: 'Suppression impossible',
        message: `Cette cellule a déjà ${cell.current_count} quota(s) complété(s). Les cellules avec des quotas complétés ne peuvent pas être supprimées.`,
        type: 'warning'
      });
      return;
    }
    setDeleteCellId(cellId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteCell = async () => {
    try {
      const response = await axios.delete(`/quotas/projects/${quotaProjectId}/cells/${deleteCellId}`);

      if (response.data.pending_deletion) {
        // chef_terrain: pending deletion created, not actually deleted
        onCellUpdated({
          ...response.data.data,
          _pendingDeletion: true,
        });
        setAlertModal({
          show: true,
          title: 'En attente d\'approbation',
          message: response.data.message,
          type: 'info'
        });
      } else {
        onCellDeleted(deleteCellId);
      }

      setShowDeleteConfirm(false);
      setDeleteCellId(null);
    } catch (error) {
      console.error('Error deleting cell:', error);
      setShowDeleteConfirm(false);
      setDeleteCellId(null);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors de la suppression',
        type: 'error'
      });
    }
  };

  // Toggle pause status for a cell
  const handleTogglePause = async (cellId) => {
    if (togglingPause) return; // Prevent double clicks
    
    try {
      setTogglingPause(cellId);
      const response = await axios.post(`/quotas/projects/${quotaProjectId}/cells/${cellId}/toggle-pause`);
      
      if (response.data.data) {
        // Update the cell in the parent state
        const cell = cells.find(c => c.id === cellId);
        if (cell) {
          onCellUpdated({
            ...cell,
            status: response.data.data.status,
            auto_status: response.data.data.auto_status,
            is_paused: response.data.data.is_paused
          });
        }
      }
    } catch (error) {
      console.error('Error toggling cell pause:', error);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors du changement de statut',
        type: 'error'
      });
    } finally {
      setTogglingPause(null);
    }
  };

  // Download the Excel assignment template (auth via axios interceptor → blob).
  const handleDownloadTemplate = async () => {
    setShowExcelMenu(false);
    setDownloadingTemplate(true);
    try {
      const response = await axios.get(
        `/quotas/projects/${quotaProjectId}/assignments/template`,
        { responseType: 'blob' }
      );
      // Use the server-provided filename (includes survey title + vague) when available.
      let filename = `assignations_projet_${quotaProjectId}.xlsx`;
      const disposition = response.headers['content-disposition'];
      if (disposition) {
        const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(disposition);
        if (match && match[1]) {
          filename = decodeURIComponent(match[1].trim());
        }
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: 'Erreur lors du téléchargement du modèle',
        type: 'error'
      });
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // Download the location-addresses Excel template.
  const handleDownloadLocationTemplate = async () => {
    setShowLocationMenu(false);
    setDownloadingLocationTemplate(true);
    try {
      const response = await axios.get(
        `/quotas/projects/${quotaProjectId}/locations/template`,
        { responseType: 'blob' }
      );
      let filename = `adresses_projet_${quotaProjectId}.xlsx`;
      const disposition = response.headers['content-disposition'];
      if (disposition) {
        const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(disposition);
        if (match && match[1]) filename = decodeURIComponent(match[1].trim());
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading location template:', error);
      setAlertModal({ show: true, title: 'Erreur', message: 'Erreur lors du téléchargement du modèle', type: 'error' });
    } finally {
      setDownloadingLocationTemplate(false);
    }
  };

  // Download the Détails Excel template: Ref + one column per quota dimension, so the
  // headers the CT starts from are already the ones the import recognises.
  const handleDownloadDetailTemplate = async () => {
    setShowDetailMenu(false);
    setDownloadingDetailTemplate(true);
    try {
      const response = await axios.get(
        `/quotas/projects/${quotaProjectId}/details/template`,
        { responseType: 'blob' }
      );
      // Fallback only — the server names the file after the survey (and vague).
      let filename = `details_template_${quotaProjectId}.xlsx`;
      const disposition = response.headers['content-disposition'];
      if (disposition) {
        const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(disposition);
        if (match && match[1]) filename = decodeURIComponent(match[1].trim());
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading detail template:', error);
      setAlertModal({ show: true, title: 'Erreur', message: 'Erreur lors du téléchargement du modèle', type: 'error' });
    } finally {
      setDownloadingDetailTemplate(false);
    }
  };

  // Download the details as currently saved. Same columns as the import expects, so the
  // file can be corrected in Excel and sent straight back through « Importer les détails ».
  const handleDownloadDetailExport = async () => {
    setShowDetailMenu(false);
    setDownloadingDetailExport(true);
    try {
      const response = await axios.get(
        `/quotas/projects/${quotaProjectId}/details/export`,
        { responseType: 'blob' }
      );
      let filename = `details_export_${quotaProjectId}.xlsx`;
      const disposition = response.headers['content-disposition'];
      if (disposition) {
        const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(disposition);
        if (match && match[1]) filename = decodeURIComponent(match[1].trim());
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting details:', error);
      setAlertModal({ show: true, title: 'Erreur', message: "Erreur lors de l'export des détails", type: 'error' });
    } finally {
      setDownloadingDetailExport(false);
    }
  };

  // Download the current location addresses as an Excel export.
  const handleDownloadLocationExport = async () => {
    setShowLocationMenu(false);
    setDownloadingLocationTemplate(true);
    try {
      const response = await axios.get(
        `/quotas/projects/${quotaProjectId}/locations/export`,
        { responseType: 'blob' }
      );
      let filename = `adresses_export_projet_${quotaProjectId}.xlsx`;
      const disposition = response.headers['content-disposition'];
      if (disposition) {
        const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(disposition);
        if (match && match[1]) filename = decodeURIComponent(match[1].trim());
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting addresses:', error);
      setAlertModal({ show: true, title: 'Erreur', message: 'Erreur lors de l\'export des adresses', type: 'error' });
    } finally {
      setDownloadingLocationTemplate(false);
    }
  };

  // Get status badge styling
  const getStatusBadge = (cell) => {
    const status = cell.status || 'pending';
    const statusConfig = {
      pending: { label: 'En attente', className: 'bg-gray-100 text-gray-700' },
      in_progress: { label: 'En cours', className: 'bg-blue-100 text-blue-700' },
      completed: { label: 'Terminé', className: 'bg-green-100 text-green-700' },
      paused: { label: 'En pause', className: 'bg-yellow-100 text-yellow-700' }
    };
    return statusConfig[status] || statusConfig.pending;
  };

  // Selection management
  const toggleCellSelection = (cellId) => {
    const cell = cells.find(c => c.id === cellId);
    if (cell && cell.current_count > 0) {
      setAlertModal({
        show: true,
        title: 'Sélection impossible',
        message: `Cette cellule a déjà ${cell.current_count} quota(s) complété(s). Les cellules avec des quotas complétés ne peuvent pas être sélectionnées pour la suppression.`,
        type: 'warning'
      });
      return;
    }
    setSelectedCells(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cellId)) {
        newSet.delete(cellId);
      } else {
        newSet.add(cellId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedCells.size === filteredCells.length) {
      setSelectedCells(new Set());
    } else {
      setSelectedCells(new Set(filteredCells.map(cell => cell.id)));
    }
  };

  const handleBulkDelete = () => {
    if (selectedCells.size === 0) return;
    
    // Check if any selected cell has completed quotas
    const cellsWithCompletes = Array.from(selectedCells)
      .map(cellId => cells.find(c => c.id === cellId))
      .filter(cell => cell && cell.current_count > 0);
    
    if (cellsWithCompletes.length > 0) {
      const cellNames = cellsWithCompletes
        .map(cell => cell.cell_code || `Cellule #${cell.id}`)
        .join(', ');
      setAlertModal({
        show: true,
        title: 'Suppression impossible',
        message: `Les cellules suivantes ont déjà des quotas complétés et ne peuvent pas être supprimées : ${cellNames}. Veuillez désélectionner ces cellules avant de continuer.`,
        type: 'warning'
      });
      return;
    }
    
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    const cellIdsToDelete = Array.from(selectedCells);
    const count = cellIdsToDelete.length;
    
    if (count === 0) return;
    
    // Double-check before deletion (in case state changed)
    const cellsWithCompletes = cellIdsToDelete
      .map(cellId => cells.find(c => c.id === cellId))
      .filter(cell => cell && cell.current_count > 0);
    
    if (cellsWithCompletes.length > 0) {
      const cellNames = cellsWithCompletes
        .map(cell => cell.cell_code || `Cellule #${cell.id}`)
        .join(', ');
      setShowBulkDeleteConfirm(false);
      setAlertModal({
        show: true,
        title: 'Suppression impossible',
        message: `Les cellules suivantes ont déjà des quotas complétés et ne peuvent pas être supprimées : ${cellNames}.`,
        type: 'warning'
      });
      return;
    }
    
    try {
      const response = await axios.delete(`/quotas/projects/${quotaProjectId}/cells/bulk`, {
        data: { cell_ids: cellIdsToDelete }
      });

      if (response.data.pending_deletion) {
        // chef_terrain: pending deletions created
        cellIdsToDelete.forEach(cellId => {
          onCellUpdated({ id: cellId, _pendingDeletion: true });
        });
        setAlertModal({
          show: true,
          title: 'En attente d\'approbation',
          message: response.data.message,
          type: 'info'
        });
      } else {
        cellIdsToDelete.forEach(cellId => {
          onCellDeleted(cellId);
        });
        setAlertModal({
          show: true,
          title: 'Succès',
          message: `${count} cellule(s) supprimée(s) avec succès`,
          type: 'success'
        });
      }

      setSelectedCells(new Set());
      setShowBulkDeleteConfirm(false);
    } catch (error) {
      console.error('Error bulk deleting cells:', error);
      setShowBulkDeleteConfirm(false);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors de la suppression des cellules',
        type: 'error'
      });
    }
  };

  const getDimensionValueLabel = (dimensionId, valueCode) => {
    const dimension = dimensions.find(d => d.id === dimensionId);
    if (!dimension || !dimension.values_json) return valueCode;
    const value = dimension.values_json.find(v => v.code === valueCode);
    return value?.label || valueCode;
  };

  const getCellDisplayInfo = (cell) => {
    const dimensionValues = cell.dimension_values_json || {};
    const labels = [];
    dimensions.forEach(dim => {
      const valueCode = dimensionValues[dim.id];
      if (valueCode) {
        labels.push(getDimensionValueLabel(dim.id, valueCode));
      }
    });
    return labels.join(' - ') || cell.cell_code;
  };

  // Build the pivoted view: prestataires as rows, combinaisons (cells) as columns.
  // Display-only — reuses the exact same per-supervisor values shown by the chips.
  // Defined after getCellDisplayInfo so it can label columns.
  const pivotData = useMemo(() => {
    const columns = filteredCells.map(cell => ({
      cell,
      id: Number(cell.id),
      label: getCellDisplayInfo(cell),
      target: cell.target_count || 0,
      actuel: cell.current_count || 0,
    }));

    const prestataireMap = new Map(); // supervisorId -> { id, name }
    const valueMap = new Map();        // `${cellId}:${supId}` -> { completes, assigned, pending }

    filteredCells.forEach(cell => {
      const cid = Number(cell.id);

      // Approved supervisors (new system or legacy single).
      const approved = cell.assigned_supervisors
        || (cell.assigned_to_supervisor_id ? [cell.assigned_supervisor].filter(Boolean) : []);
      approved.forEach(sup => {
        if (!sup || sup.id == null) return;
        if (!prestataireMap.has(sup.id)) prestataireMap.set(sup.id, { id: sup.id, name: sup.name || 'N/A' });
        valueMap.set(`${cid}:${sup.id}`, {
          completes: sup.completes || 0,
          assigned: (sup.assigned_count ?? null),
          pending: false,
        });
      });

      // Pending assignments take precedence for that supervisor (mirrors the chip logic).
      pendingAssignments
        .filter(pa => Number(pa.quota_cell_id) === cid)
        .forEach(pa => {
          const sid = pa.supervisor_id || pa.assigned_to_supervisor_id
            || pa.supervisor?.id || pa.assignedSupervisor?.id || pa.assigned_supervisor?.id;
          const name = pa.supervisor?.name || pa.assigned_supervisor?.name || pa.assignedSupervisor?.name || 'N/A';
          if (sid == null) return;
          if (!prestataireMap.has(sid)) prestataireMap.set(sid, { id: sid, name });
          valueMap.set(`${cid}:${sid}`, {
            completes: pa.completes || 0,
            assigned: (pa.assigned_count ?? null),
            pending: true,
          });
        });
    });

    const prestataires = Array.from(prestataireMap.values())
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    const grandActuel = columns.reduce((s, c) => s + c.actuel, 0);
    const grandTarget = columns.reduce((s, c) => s + c.target, 0);

    return { columns, prestataires, valueMap, grandActuel, grandTarget };
  }, [filteredCells, pendingAssignments, dimensions]); // eslint-disable-line react-hooks/exhaustive-deps

  if (dimensions.length === 0) {
    return (
      <div className="quota-matrix-empty">
        <p>Aucune dimension définie</p>
        <p className="hint">Définissez d'abord des dimensions dans le panneau de gauche</p>
      </div>
    );
  }

  return (
    <div className="quota-matrix">
      <div className="matrix-header">
        <h2>Cellules de Quota</h2>
        <div className="matrix-header-actions">
          {/* View toggle: pivot (prestataires × combinaisons) vs classic list.
              Same segmented-control design as the payment-batch section switch. */}
          <div className="section-segment-group mr-2">
            <button
              type="button"
              className={`section-segment ${viewMode === 'list' ? 'section-segment--checked' : ''}`}
              onClick={() => setViewMode('list')}
              title="Gestion : une ligne par combinaison (avec les actions de gestion)"
            >
              Gestion
            </button>
            <button
              type="button"
              className={`section-segment ${viewMode === 'pivot' ? 'section-segment--checked' : ''}`}
              onClick={() => setViewMode('pivot')}
              title="Synthèse : prestataires en lignes, combinaisons en colonnes"
            >
              Synthèse
            </button>
            <button
              type="button"
              className={`section-segment ${viewMode === 'map' ? 'section-segment--checked' : ''}`}
              onClick={() => setViewMode('map')}
              title="Carte : emplacements/adresses de ce projet"
            >
              Carte
            </button>
            <button
              type="button"
              className={`section-segment ${viewMode === 'details' ? 'section-segment--checked' : ''}`}
              onClick={() => setViewMode('details')}
              title="Détails : informations libres importées par prestataire (ne comptent pas dans les quotas)"
            >
              Détails
            </button>
          </div>
          {!isLocked && (
            <>
              <button
                className="btn-secondary btn-sm generate-cells-btn relative overflow-hidden"
                onClick={handleGenerateAllCells}
                disabled={generating || !dimensions || dimensions.length === 0}
                title="Générer toutes les combinaisons possibles"
              >
                <span className="magic-icon-wrapper relative inline-flex items-center">
                  <FontAwesomeIcon icon={faMagic} className="magic-wand-icon" />
                  <span className="sparkle sparkle-1">✨</span>
                  <span className="sparkle sparkle-2">✨</span>
                  <span className="sparkle sparkle-3">✨</span>
                </span>
                {generating ? 'Génération...' : 'Générer cellules'}
              </button>
              <button
                className="btn-primary btn-sm"
                onClick={() => setShowAddModal(true)}
                title="Ajouter une cellule"
              >
                <FontAwesomeIcon icon={faPlus} />
                Ajouter une cellule
              </button>
            </>
          )}
          {/* Bulk Assign Supervisors Button — commented out on request (kept for reference)
          {(() => {
            const shouldShow = onBulkAssignSupervisors && canAssignSupervisor &&
              !['charge_etude', 'charge_etude_superieur'].includes(user?.role) &&
              cells.length > 0;
            
            // Debug logging
            if (user && cells.length > 0) {
              console.log('QuotaMatrix - Bulk Assign Button Debug:', {
                onBulkAssignSupervisors: !!onBulkAssignSupervisors,
                canAssignSupervisor,
                userRole: user?.role,
                assignmentStatus,
                projectStatus,
                projectLockedFinishedOrArchived,
                cellsCount: cells.length,
                shouldShow,
                isLocked
              });
            }
            
            return shouldShow ? (
              <button
                className="btn-primary btn-sm"
                onClick={onBulkAssignSupervisors}
                title="Assigner des prestataires à toutes les cellules"
                style={{ backgroundColor: '#1e40af', borderColor: '#1e40af' }}
              >
                <FontAwesomeIcon icon={faUsers} />
                Assigner en masse
              </button>
            ) : null;
          })()}
          */}
          {/* Excel assignment dropdown: template + export always, import only while the
              project can still be assigned. */}
          {canDownloadAssignments && cells.length > 0 && (
            <div className="relative inline-block">
              <button
                ref={excelBtnRef}
                className="btn-primary btn-sm"
                onClick={() => showExcelMenu ? setShowExcelMenu(false) : openHeaderMenu(excelBtnRef, setExcelMenuStyle, setShowExcelMenu)}
                title="Assignation par fichier Excel"
                style={{ backgroundColor: '#047857', borderColor: '#047857' }}
              >
                <FontAwesomeIcon icon={faFileExcel} />
                Assignation Excel
                <FontAwesomeIcon icon={faChevronDown} className="ml-1 text-xs" />
              </button>
              {showExcelMenu && excelMenuStyle && createPortal(
                <>
                  <div className="fixed inset-0 z-[1999]" onClick={() => setShowExcelMenu(false)} />
                  <div style={excelMenuStyle} className="w-60 bg-white border border-gray-200 rounded-md shadow-lg z-[2000] py-1">
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                      onClick={handleDownloadTemplate}
                      disabled={downloadingTemplate}
                    >
                      <FontAwesomeIcon icon={faDownload} className="text-gray-500" />
                      {downloadingTemplate ? 'Téléchargement...' : 'Télécharger le modèle'}
                    </button>
                    {canAssignSupervisor && (
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        onClick={() => { setShowExcelMenu(false); setShowExcelImport(true); }}
                      >
                        <FontAwesomeIcon icon={faFileImport} className="text-gray-500" />
                        Importer les assignations
                      </button>
                    )}
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                      onClick={handleDownloadTemplate}
                      disabled={downloadingTemplate}
                    >
                      <FontAwesomeIcon icon={faFileExcel} className="text-gray-500" />
                      Exporter les assignations
                    </button>
                  </div>
                </>,
                document.body
              )}
            </div>
          )}
          {/* Location addresses dropdown: template download + import.
              CT & CTS can manage addresses whenever the project isn't finished/archived
              (locked/in-progress fieldwork is fine). Only shown in the Carte / Gestion views. */}
          {(viewMode === 'map' || viewMode === 'list')
            && (user?.role === 'chef_terrain' || user?.role === 'chef_terrain_superieur')
            && !['finished', 'archived'].includes(projectStatus)
            && cells.length > 0 && (
            <div className="relative inline-block">
              <button
                ref={locationBtnRef}
                className="btn-primary btn-sm"
                onClick={() => showLocationMenu ? setShowLocationMenu(false) : openHeaderMenu(locationBtnRef, setLocationMenuStyle, setShowLocationMenu)}
                title="Adresses des emplacements (Excel)"
                style={{ backgroundColor: '#b91c1c', borderColor: '#b91c1c' }}
              >
                <FontAwesomeIcon icon={faLocationDot} />
                Adresses
                <FontAwesomeIcon icon={faChevronDown} className="ml-1 text-xs" />
              </button>
              {showLocationMenu && locationMenuStyle && createPortal(
                <>
                  <div className="fixed inset-0 z-[1999]" onClick={() => setShowLocationMenu(false)} />
                  <div style={locationMenuStyle} className="w-60 bg-white border border-gray-200 rounded-md shadow-lg z-[2000] py-1">
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                      onClick={handleDownloadLocationTemplate}
                      disabled={downloadingLocationTemplate}
                    >
                      <FontAwesomeIcon icon={faDownload} className="text-gray-500" />
                      {downloadingLocationTemplate ? 'Téléchargement...' : 'Télécharger le modèle'}
                    </button>
                    {projectStatus === 'draft' && (
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        onClick={() => { setShowLocationMenu(false); setShowLocationImport(true); }}
                      >
                        <FontAwesomeIcon icon={faFileImport} className="text-gray-500" />
                        Importer les adresses
                      </button>
                    )}
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                      onClick={handleDownloadLocationExport}
                      disabled={downloadingLocationTemplate}
                    >
                      <FontAwesomeIcon icon={faFileExcel} className="text-gray-500" />
                      Exporter les adresses
                    </button>
                    {projectStatus === 'draft' && (
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        onClick={() => { setShowLocationMenu(false); setShowLocationManage(true); }}
                      >
                        <FontAwesomeIcon icon={faListUl} className="text-gray-500" />
                        Gérer les adresses
                      </button>
                    )}
                  </div>
                </>,
                document.body
              )}
            </div>
          )}
          {/* Détails dropdown: template download + import. Same gate as Adresses (CT & CTS,
              project not finished/archived). Only shown in the Détails view. */}
          {viewMode === 'details'
            && (user?.role === 'chef_terrain' || user?.role === 'chef_terrain_superieur')
            && !['finished', 'archived'].includes(projectStatus) && (
            <div className="relative inline-block">
              <button
                ref={detailBtnRef}
                className="btn-primary btn-sm"
                onClick={() => showDetailMenu ? setShowDetailMenu(false) : openHeaderMenu(detailBtnRef, setDetailMenuStyle, setShowDetailMenu)}
                title="Détails du projet (Excel)"
                style={{ backgroundColor: '#4f46e5', borderColor: '#4f46e5' }}
              >
                <FontAwesomeIcon icon={faTableList} />
                Détails
                <FontAwesomeIcon icon={faChevronDown} className="ml-1 text-xs" />
              </button>
              {showDetailMenu && detailMenuStyle && createPortal(
                <>
                  <div className="fixed inset-0 z-[1999]" onClick={() => setShowDetailMenu(false)} />
                  <div style={detailMenuStyle} className="w-60 bg-white border border-gray-200 rounded-md shadow-lg z-[2000] py-1">
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                      onClick={handleDownloadDetailTemplate}
                      disabled={downloadingDetailTemplate}
                    >
                      <FontAwesomeIcon icon={faDownload} className="text-gray-500" />
                      {downloadingDetailTemplate ? 'Téléchargement...' : 'Télécharger le modèle'}
                    </button>
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      onClick={() => { setShowDetailMenu(false); setShowDetailImport(true); }}
                    >
                      <FontAwesomeIcon icon={faFileImport} className="text-gray-500" />
                      Importer les détails
                    </button>
                    {/* Nothing saved yet means nothing to export — the file would be a
                        lone « Ref » header, which just looks broken. */}
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleDownloadDetailExport}
                      disabled={downloadingDetailExport || detailRowCount === 0}
                      title={detailRowCount === 0 ? 'Aucun détail importé pour ce projet' : 'Exporter les détails enregistrés'}
                    >
                      <FontAwesomeIcon icon={faFileExcel} className="text-gray-500" />
                      {downloadingDetailExport ? 'Export...' : 'Exporter les détails'}
                    </button>
                  </div>
                </>,
                document.body
              )}
            </div>
          )}
        </div>
      </div>

      {viewMode === 'list' && !isLocked && selectedCells.size > 0 && (
        <div className="mt-3 mb-3 flex items-center gap-3">
          <span className="text-sm text-gray-700">{selectedCells.size} cellule(s) sélectionnée(s)</span>
          <button
            className="btn-danger btn-sm"
            onClick={handleBulkDelete}
            title="Supprimer les cellules sélectionnées"
          >
            <FontAwesomeIcon icon={faTrashAlt} />
            Supprimer ({selectedCells.size})
          </button>
          <button
            className="btn-secondary btn-sm"
            onClick={() => setSelectedCells(new Set())}
          >
            Annuler
          </button>
        </div>
      )}

      {viewMode === 'map' && (
        <div className="mt-2">
          <AddressesMapView
            endpoint={`/quotas/locations/all?project_id=${quotaProjectId}`}
            showSupervisor
            allowVisitedToggle={['chef_terrain', 'chef_terrain_superieur', 'administrateur_general', 'administrateur_general_superieur', 'admin_it'].includes(user?.role)}
            visitedBase="/quotas/locations"
            allowDelete={['chef_terrain', 'chef_terrain_superieur', 'administrateur_general', 'administrateur_general_superieur', 'admin_it'].includes(user?.role)}
            embedded
            heightStyle="65vh"
            refreshKey={mapRefreshKey}
            onChanged={onImportComplete}
            showNotes
            canEditNotes={['chef_terrain', 'chef_terrain_superieur', 'administrateur_general', 'administrateur_general_superieur', 'admin_it'].includes(user?.role)}
          />
        </div>
      )}

      {viewMode === 'details' && (
        <DetailsView
          quotaProjectId={quotaProjectId}
          refreshKey={detailRefreshKey}
          // Feeds the import modal's "this replaces N existing lines" warning.
          onLoaded={setDetailRowCount}
        />
      )}

      {viewMode === 'pivot' && (
        pivotData.columns.length === 0 ? (
          <div className="overflow-auto max-h-[70vh]">
            <p className="px-4 py-8 text-center text-gray-500">Aucune cellule trouvée</p>
          </div>
        ) : (
          <div className="w-full overflow-auto max-h-[70vh]">
            <table className="w-full min-w-max border-collapse text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 sticky top-0 left-0 z-30 bg-gray-50 min-w-[180px]">
                    Prestataire
                  </th>
                  {pivotData.columns.map(col => (
                    <th
                      key={col.id}
                      className="px-3 py-3 text-center text-xs font-semibold text-gray-700 hover:text-gray-900 hover:bg-gray-100 uppercase tracking-wider border-b border-gray-200 sticky top-0 z-20 bg-gray-50 whitespace-nowrap min-w-[140px] cursor-pointer"
                      title={`${col.label} — voir les détails de la cellule`}
                      onClick={() => onCellSelect(col.cell)}
                    >
                      {col.label}
                    </th>
                  ))}
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-l border-gray-200 sticky top-0 z-20 bg-gray-100 whitespace-nowrap min-w-[100px]">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pivotData.prestataires.length === 0 ? (
                  <tr>
                    <td colSpan={pivotData.columns.length + 2} className="px-4 py-8 text-center text-gray-500">
                      Aucun prestataire assigné
                    </td>
                  </tr>
                ) : (
                  pivotData.prestataires.map(p => {
                    let rowCompletes = 0;
                    let rowAssigned = 0;
                    return (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td
                          className="px-4 py-3 text-sm font-medium text-gray-800 hover:text-gray-900 hover:bg-gray-100 border-r border-gray-200 sticky left-0 z-10 bg-white whitespace-nowrap cursor-pointer"
                          onClick={() => navigate(`/supervisors/${p.id}`)}
                          title={`Voir la fiche de ${p.name}`}
                        >
                          {p.name}
                        </td>
                        {pivotData.columns.map(col => {
                          const v = pivotData.valueMap.get(`${col.id}:${p.id}`);
                          if (!v) {
                            return (
                              <td key={col.id} className="px-3 py-3 text-center text-gray-300 align-middle">—</td>
                            );
                          }
                          rowCompletes += v.completes || 0;
                          rowAssigned += (v.assigned ?? 0);
                          const isOverflow = v.assigned !== null && v.assigned !== undefined && v.completes > v.assigned;
                          return (
                            <td
                              key={col.id}
                              className={`px-3 py-3 text-center align-middle text-xs font-medium ${
                                isOverflow
                                  ? 'bg-red-100 text-red-700 quota-overflow-blink'
                                  : v.pending
                                  ? 'bg-yellow-50 text-yellow-700'
                                  : 'text-gray-800'
                              }`}
                              title={isOverflow ? `${p.name} a dépassé son quota assigné (${v.completes}/${v.assigned})` : undefined}
                            >
                              {v.completes}/{v.assigned ?? '—'}
                            </td>
                          );
                        })}
                        <td className="px-3 py-3 text-center border-l border-gray-200 bg-gray-50 whitespace-nowrap text-sm font-semibold text-gray-900">
                          {rowCompletes}/{rowAssigned}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              <tfoot>
                <tr className="bg-gray-100 border-t-2 border-gray-300">
                  <td className="px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 sticky left-0 z-10 bg-gray-100 whitespace-nowrap">
                    Total
                  </td>
                  {pivotData.columns.map(col => {
                    const percent = col.target > 0 ? (col.actuel / col.target) * 100 : 0;
                    const restant = Math.max(0, col.target - col.actuel);
                    return (
                      <td key={col.id} className="px-3 py-3 text-center align-middle whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">{col.actuel}/{col.target}</div>
                        <div className="flex items-center gap-1.5 mt-1">
                          <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden min-w-[40px]">
                            <div
                              className="h-full bg-green-500 transition-all duration-300"
                              style={{ width: `${Math.min(100, percent)}%` }}
                            ></div>
                          </div>
                          <span className="text-[11px] text-gray-600">{percent.toFixed(1)}%</span>
                        </div>
                        <div className="text-[11px] text-gray-500 mt-0.5">Restant : {restant}</div>
                      </td>
                    );
                  })}
                  <td className="px-3 py-3 text-center text-sm font-bold text-gray-900 border-l border-gray-200 bg-gray-200 whitespace-nowrap">
                    {pivotData.grandActuel}/{pivotData.grandTarget}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}

      {viewMode === 'list' && (
      <div className="overflow-auto max-h-[70vh]">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50">
              {!isLocked && (
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 sticky top-0 z-20 bg-gray-50 w-12">
                  <input
                    type="checkbox"
                    checked={filteredCells.length > 0 && selectedCells.size === filteredCells.length}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </th>
              )}
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 sticky top-0 z-20 bg-gray-50">Combinaison</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 sticky top-0 z-20 bg-gray-50">Cible</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 sticky top-0 z-20 bg-gray-50">Actuel</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 sticky top-0 z-20 bg-gray-50">Restant</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 sticky top-0 z-20 bg-gray-50">% Alloué</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 sticky top-0 z-20 bg-gray-50">Prestataires</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 sticky top-0 z-20 bg-gray-50">Statut</th>
              {!isLocked && (
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 sticky top-0 z-20 bg-gray-50">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredCells.length === 0 ? (
              <tr>
                <td colSpan={isLocked ? 7 : 9} className="px-4 py-8 text-center text-gray-500">
                  Aucune cellule trouvée
                </td>
              </tr>
            ) : (
              filteredCells.map(cell => {
                const progress = calculator?.getCellProgress(cell.id);
                const progressPercent = progress?.progress || 0;
                const isComplete = progress?.isComplete || false;
                const isSelected = selectedCells.has(cell.id);
                
                // Find pending assignments for this cell (support multiple)
                // Normalize IDs to numbers for consistent comparison
                const cellId = Number(cell.id);
                const cellPendingAssignments = pendingAssignments.filter(pa => Number(pa.quota_cell_id) === cellId);
                const hasPendingAssignments = cellPendingAssignments.length > 0;
                
                // Get approved supervisors (from new system or legacy)
                const approvedSupervisors = cell.assigned_supervisors || (cell.assigned_to_supervisor_id ? [cell.assigned_supervisor].filter(Boolean) : []);
                
                // Check for pending deletion
                const hasPendingDeletion = pendingModifications.some(
                  pm => Number(pm.quota_cell_id) === cellId && pm.field === 'delete'
                );

                // Check for pending creation
                const hasPendingCreation = pendingModifications.some(
                  pm => Number(pm.quota_cell_id) === cellId && pm.field === 'create'
                );

                // Create a key that includes pending assignments to force re-render when they change
                const rowKey = `${cell.id}-${cellPendingAssignments.length}-${cellPendingAssignments.map(pa => pa.id).join(',')}-${hasPendingDeletion ? 'del' : ''}${hasPendingCreation ? 'add' : ''}`;

                return (
                  <tr
                    key={rowKey}
                    className={`hover:bg-gray-50 ${hasPendingCreation ? 'bg-emerald-50 border-l-4 border-l-emerald-400' : hasPendingDeletion ? 'bg-red-50 opacity-75' : isSelected ? 'bg-blue-50' : cell.is_paused ? 'bg-yellow-50' : isComplete ? 'bg-green-50' : ''}`}
                  >
                    {!isLocked && (
                      <td className="px-4 py-3 text-center align-middle" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleCellSelection(cell.id)}
                          disabled={cell.current_count > 0 || hasPendingDeletion || hasPendingCreation}
                          className={`w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${
                            (cell.current_count > 0 || hasPendingDeletion || hasPendingCreation) ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          onClick={(e) => e.stopPropagation()}
                          title={hasPendingCreation ? 'Ajout en attente d\'approbation' : hasPendingDeletion ? 'Suppression en attente d\'approbation' : cell.current_count > 0 ? `Cette cellule a ${cell.current_count} quota(s) complété(s) et ne peut pas être supprimée` : 'Sélectionner pour suppression'}
                        />
                      </td>
                    )}
                    <td
                      className={`px-4 py-3 text-sm font-medium align-middle cursor-pointer ${hasPendingDeletion ? 'line-through text-gray-400' : 'text-gray-900'}`}
                      onClick={() => onCellSelect(cell)}
                    >
                      {getCellDisplayInfo(cell)}
                      {(() => {
                        const hasPendingDim = pendingModifications.some(
                          pm => Number(pm.quota_cell_id) === cellId && pm.field === 'dimension_values_json'
                        );
                        const hasPendingNotes = pendingModifications.some(
                          pm => Number(pm.quota_cell_id) === cellId && pm.field === 'notes'
                        );
                        if (!hasPendingDim && !hasPendingNotes && !hasPendingDeletion && !hasPendingCreation) return null;
                        return (
                          <span className="ml-1.5 inline-flex items-center gap-1">
                            {hasPendingCreation && (
                              <span className="inline-flex items-center gap-0.5 text-xs text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded border border-emerald-300" title="Ajout en attente d'approbation">
                                <FontAwesomeIcon icon={faClock} className="text-emerald-600" />
                                ajout
                              </span>
                            )}
                            {hasPendingDeletion && (
                              <span className="inline-flex items-center gap-0.5 text-xs text-red-700 bg-red-100 px-1.5 py-0.5 rounded border border-red-300" title="Suppression en attente d'approbation">
                                <FontAwesomeIcon icon={faClock} className="text-red-600" />
                                suppr.
                              </span>
                            )}
                            {hasPendingDim && (
                              <span className="inline-flex items-center gap-0.5 text-xs text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded border border-yellow-300" title="Modification de combinaison en attente">
                                <FontAwesomeIcon icon={faClock} className="text-yellow-600" />
                                combi.
                              </span>
                            )}
                            {hasPendingNotes && (
                              <span className="inline-flex items-center gap-0.5 text-xs text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded border border-yellow-300" title="Modification des notes en attente">
                                <FontAwesomeIcon icon={faClock} className="text-yellow-600" />
                                notes
                              </span>
                            )}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 align-middle">
                      {cell.target_count?.toLocaleString() || 0}
                      {(() => {
                        const pendingTargetMod = pendingModifications.find(
                          pm => Number(pm.quota_cell_id) === cellId && pm.field === 'target_count'
                        );
                        if (pendingTargetMod) {
                          return (
                            <span className="ml-1 inline-flex items-center gap-1 text-xs text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded border border-yellow-300" title={`Modification en attente: ${pendingTargetMod.old_value} → ${pendingTargetMod.new_value}`}>
                              <FontAwesomeIcon icon={faClock} className="text-yellow-600" />
                              →{pendingTargetMod.new_value}
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 align-middle">{cell.current_count?.toLocaleString() || 0}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 align-middle">{progress?.remaining?.toLocaleString() || 0}</td>
                    <td className="px-4 py-3 text-sm align-middle">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-green-500 transition-all duration-300"
                            style={{ width: `${Math.min(100, progressPercent)}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-600 min-w-[45px]">{progressPercent.toFixed(1)}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm align-middle">
                      <div className="flex flex-col items-center gap-1">
                        {/* Pending assignments (proposed/not approved yet) */}
                        {hasPendingAssignments && cellPendingAssignments.map((pendingAssignment, idx) => {
                          const supervisorName = pendingAssignment.supervisor?.name || pendingAssignment.assigned_supervisor?.name || pendingAssignment.assignedSupervisor?.name || 'N/A';
                          const assignedCount = pendingAssignment.assigned_count;
                          const completes = pendingAssignment.completes || 0;
                          const isOverflow = assignedCount !== null && assignedCount !== undefined && completes > assignedCount;
                          return (
                            <div
                              key={idx}
                              className={`flex items-center gap-1.5 px-2 py-1 rounded border w-full min-w-[120px] max-w-[150px] justify-center ${isOverflow ? 'bg-red-100 border-red-400 quota-overflow-blink' : 'bg-yellow-100 border-yellow-300'}`}
                              title={isOverflow ? `${supervisorName} a dépassé son quota assigné (${completes}/${assignedCount})` : undefined}
                            >
                              <FontAwesomeIcon icon={faClock} className={`text-xs ${isOverflow ? 'text-red-600' : 'text-yellow-600'}`} />
                              <span className={`text-xs font-medium ${isOverflow ? 'text-red-700' : 'text-yellow-700'}`}>
                                {supervisorName}
                                {assignedCount !== null && assignedCount !== undefined && (
                                  <span className={`ml-1 ${isOverflow ? 'text-red-700 font-bold' : 'text-yellow-600'}`}>({completes}/{assignedCount})</span>
                                )}
                              </span>
                            </div>
                          );
                        })}
                        {/* Approved supervisors - only show if no pending assignments, or if they're also in pending list */}
                        {approvedSupervisors.length > 0 && approvedSupervisors.map((supervisor, idx) => {
                          // Check if this supervisor has a pending assignment
                          const hasPendingForThisSupervisor = cellPendingAssignments.some(
                            pa => (pa.supervisor_id || pa.assigned_to_supervisor_id) === supervisor.id
                          );
                          
                          // If there are pending assignments, only show approved supervisors that are also pending
                          // (This hides unselected supervisors that were kept due to completes)
                          if (hasPendingAssignments && !hasPendingForThisSupervisor) {
                            return null; // Hide unselected supervisors when there are pending assignments
                          }
                          
                          // Skip if this supervisor is already shown as pending
                          if (hasPendingForThisSupervisor) return null;
                          
                          const assignedCount = supervisor.assigned_count;
                          const completes = supervisor.completes || 0;
                          const isOverflow = assignedCount !== null && assignedCount !== undefined && completes > assignedCount;
                          return (
                            <div
                              key={idx}
                              className={`flex items-center gap-1 px-2 py-1 rounded border w-full min-w-[120px] max-w-[150px] justify-center ${isOverflow ? 'bg-red-100 border-red-400 quota-overflow-blink' : 'bg-green-50 border-green-200'}`}
                              title={isOverflow ? `${supervisor.name || 'Ce prestataire'} a dépassé son quota assigné (${completes}/${assignedCount})` : undefined}
                            >
                              <span className={`text-xs font-medium ${isOverflow ? 'text-red-700' : 'text-green-700'}`}>
                                {supervisor.name || 'N/A'}
                                {assignedCount !== null && assignedCount !== undefined && (
                                  <span className={`ml-1 ${isOverflow ? 'text-red-700 font-bold' : 'text-green-600'}`}>({completes}/{assignedCount})</span>
                                )}
                              </span>
                            </div>
                          );
                        })}
                        {/* No assignment */}
                        {approvedSupervisors.length === 0 && !hasPendingAssignments && (
                          <span className="text-xs text-gray-400 italic">Non assigné</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm align-middle" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(cell).className}`}>
                          {getStatusBadge(cell).label}
                        </span>
                        {isLocked && canTogglePause && (
                          <button
                            className={`p-1.5 rounded transition-colors ${
                              cell.is_paused 
                                ? 'text-green-600 hover:text-green-700 hover:bg-green-50' 
                                : 'text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50'
                            }`}
                            onClick={() => handleTogglePause(cell.id)}
                            disabled={togglingPause === cell.id}
                            title={cell.is_paused ? 'Reprendre' : 'Mettre en pause'}
                          >
                            <FontAwesomeIcon 
                              icon={cell.is_paused ? faPlay : faPause} 
                              className={togglingPause === cell.id ? 'animate-pulse' : ''}
                            />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm align-middle" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-2">
                        {/* Supervisor assignment button */}
                        {canAssignSupervisor && (
                          <button
                            className="p-1.5 text-blue-700 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                            onClick={() => {
                              setAssigningCell(cell);
                              setShowAssignModal(true);
                            }}
                            title={hasPendingAssignments ? "Modifier l'assignation proposée" : "Assigner un prestataire"}
                          >
                            <FontAwesomeIcon icon={faUserCheck} />
                          </button>
                        )}
                        {/* Approved assignment badge (only show if not pending and project is not locked/finished/archived) */}
                        {approvedSupervisors.length > 0 && !hasPendingAssignments && !projectLockedFinishedOrArchived && (
                          <span 
                            className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700"
                            title={`${approvedSupervisors.length} assignation(s) approuvée(s)`}
                          >
                            ✓ {approvedSupervisors.length > 1 ? approvedSupervisors.length : ''}
                          </span>
                        )}
                        {/* Legacy single supervisor badge */}
                        {/* Edit/Delete buttons - only when not locked */}
                        {!isLocked && (
                          <>
                            <button
                              className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              onClick={() => setEditingCell(cell)}
                              title="Modifier"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              className="p-1.5 text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded transition-colors"
                              onClick={() => {
                                // Duplicate cell - open modal with pre-filled values
                              setEditingCell({
                                ...cell,
                                id: undefined, // Remove ID to create new cell
                                dimension_values_json: { ...cell.dimension_values_json },
                                target_count: cell.target_count,
                                notes: cell.notes || ''
                              });
                            }}
                            title="Dupliquer"
                          >
                            <FontAwesomeIcon icon={faClone} />
                          </button>
                          {hasPendingDeletion ? (
                            <button
                              className="p-1.5 text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded transition-colors"
                              onClick={(e) => { e.stopPropagation(); onCancelPendingDeletion && onCancelPendingDeletion(cell.id); }}
                              title="Annuler la suppression en attente"
                            >
                              <FontAwesomeIcon icon={faUndo} />
                            </button>
                          ) : (
                            <button
                              className={`p-1.5 rounded transition-colors ${
                                cell.current_count > 0
                                  ? 'text-gray-400 cursor-not-allowed'
                                  : 'text-red-600 hover:text-red-700 hover:bg-red-50'
                              }`}
                              onClick={() => handleCellDelete(cell.id)}
                              disabled={cell.current_count > 0}
                              title={cell.current_count > 0 ? `Cette cellule a ${cell.current_count} quota(s) complété(s) et ne peut pas être supprimée` : 'Supprimer'}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      )}

      {showAddModal && (
        <AddCellModal
          quotaProjectId={quotaProjectId}
          dimensions={dimensions}
          cells={cells}
          contextFilters={contextFilters}
          pendingModifications={pendingModifications}
          onClose={() => setShowAddModal(false)}
          onSuccess={(newCell) => {
            onCellAdded(newCell);
            setShowAddModal(false);
          }}
        />
      )}

      {editingCell && (
        <AddCellModal
          quotaProjectId={quotaProjectId}
          dimensions={dimensions}
          cells={cells}
          cell={editingCell.id ? editingCell : null}
          cloneFrom={!editingCell.id ? editingCell : null}
          pendingModifications={pendingModifications}
          onClose={() => setEditingCell(null)}
          onSuccess={(newOrUpdatedCell) => {
            if (editingCell.id) {
              onCellUpdated(newOrUpdatedCell);
            } else {
              // It's a clone, add as new cell
              onCellAdded(newOrUpdatedCell);
            }
            setEditingCell(null);
          }}
        />
      )}

      {/* Generate Cells Confirmation Modal */}
      {showGenerateModal && generateInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" {...generateDismiss}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Générer toutes les cellules</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-700 mb-4">
                Cette action va créer <strong className="text-gray-900">{generateInfo.combinationsCount} cellule(s)</strong> en utilisant les quotas individuels définis pour chaque valeur de dimension.
                <br />
                <span className="text-xs text-gray-500 mt-1 block">
                  Chaque cellule recevra le quota minimum des valeurs de dimension qui la composent.
                </span>
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Nombre de cellules :</span>
                    <span className="font-medium text-gray-900">{generateInfo.combinationsCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total alloué :</span>
                    <span className="font-medium text-gray-900">{generateInfo.totalAllocated?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Quota générale :</span>
                    <span className="font-medium text-gray-900">{generateInfo.overallTarget.toLocaleString()}</span>
                  </div>
                  {generateInfo.totalAllocated !== generateInfo.overallTarget && (
                    <div className="flex justify-between pt-2 border-t border-gray-200">
                      <span className={`font-medium ${generateInfo.totalAllocated > generateInfo.overallTarget ? 'text-red-600' : 'text-yellow-600'}`}>
                        Différence :
                      </span>
                      <span className={`font-medium ${generateInfo.totalAllocated > generateInfo.overallTarget ? 'text-red-600' : 'text-yellow-600'}`}>
                        {(generateInfo.totalAllocated - generateInfo.overallTarget).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                Les cellules existantes avec les mêmes combinaisons seront ignorées.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                onClick={() => {
                  setShowGenerateModal(false);
                  setGenerateInfo(null);
                }}
                disabled={generating}
              >
                Annuler
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                onClick={confirmGenerateCells}
                disabled={generating}
              >
                {generating ? 'Génération...' : 'Générer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        show={alertModal.show}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ show: false, title: '', message: '', type: 'info' })}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        show={showDeleteConfirm}
        title="Supprimer la cellule"
        message="Êtes-vous sûr de vouloir supprimer cette cellule ?"
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        onConfirm={confirmDeleteCell}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setDeleteCellId(null);
        }}
      />

      {/* Bulk Delete Confirmation Modal */}
      <ConfirmationModal
        show={showBulkDeleteConfirm}
        title="Supprimer les cellules"
        message={`Êtes-vous sûr de vouloir supprimer ${selectedCells.size} cellule(s) ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        onConfirm={confirmBulkDelete}
        onCancel={() => setShowBulkDeleteConfirm(false)}
      />

      {/* Success/Error Modal */}
      {showSuccessModal && successInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" {...successDismiss}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className={`px-6 py-4 border-b ${successInfo.isError ? 'border-red-200' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-semibold flex items-center gap-2 ${successInfo.isError ? 'text-red-900' : 'text-gray-900'}`}>
                {successInfo.isError ? (
                  <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
                {successInfo.isError ? 'Erreur' : 'Succès'}
              </h3>
            </div>
            <div className="px-6 py-4">
              {successInfo.isError ? (
                <p className="text-sm text-gray-700">
                  {successInfo.message}
                </p>
              ) : successInfo.skippedCount > 0 ? (
                <div>
                  <p className="text-sm text-gray-700 mb-2">
                    <strong className="text-gray-900">{successInfo.createdCount}</strong> cellule(s) créée(s) avec succès.
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong className="text-gray-700">{successInfo.skippedCount}</strong> cellule(s) déjà existante(s) ignorée(s).
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-700">
                  <strong className="text-gray-900">{successInfo.createdCount}</strong> cellule(s) créée(s) avec succès !
                </p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  successInfo.isError 
                    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' 
                    : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                }`}
                onClick={() => {
                  setShowSuccessModal(false);
                  setSuccessInfo(null);
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showExcelImport && (
        <ExcelAssignmentModal
          quotaProjectId={quotaProjectId}
          onClose={() => setShowExcelImport(false)}
          onImportComplete={onImportComplete}
        />
      )}

      {showLocationImport && (
        <LocationImportModal
          quotaProjectId={quotaProjectId}
          onClose={() => setShowLocationImport(false)}
          onImportComplete={handleAddressesChanged}
        />
      )}

      {showLocationManage && (
        <LocationManageModal
          quotaProjectId={quotaProjectId}
          onClose={() => setShowLocationManage(false)}
          onChange={handleAddressesChanged}
          // Reassignment only needs the map markers redrawn — no project refetch.
          onAssigned={() => setMapRefreshKey(k => k + 1)}
        />
      )}

      {showDetailImport && (
        <DetailImportModal
          quotaProjectId={quotaProjectId}
          existingRows={detailRowCount}
          onClose={() => setShowDetailImport(false)}
          // Details are extras: only this view needs refreshing, never the whole project.
          onImportComplete={() => setDetailRefreshKey(k => k + 1)}
        />
      )}

      {showAssignModal && assigningCell && (
        <AssignSupervisorModal
          quotaProjectId={quotaProjectId}
          cell={assigningCell}
          pendingAssignments={pendingAssignments}
          pendingModifications={pendingModifications}
          onClose={() => {
            setShowAssignModal(false);
            setAssigningCell(null);
          }}
          onSuccess={(updatedCell) => {
            // Update cell - this will trigger state update in parent
            onCellUpdated(updatedCell);
            // Close modal
            setShowAssignModal(false);
            setAssigningCell(null);
          }}
        />
      )}

      <AlertModal
        show={alertModal.show}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ show: false, title: '', message: '', type: 'info' })}
      />
    </div>
  );
};

export default QuotaMatrix;

